/**
 * Company packages (OCP-1036): a price agreed with one named customer account.
 *
 * There is no automatic volume discount - every self-serve seat count is the
 * list price per seat. A larger customer gets a package offered here: a plan,
 * a fixed seat count, the agreed price (per seat or as the total per billing
 * period), the contract term and whether onboarding and training are included.
 * The account's checkout for that plan then charges it.
 *
 * Same routes and audit rows as the MCP tools ocp_list_company_packages,
 * ocp_create_company_package and ocp_withdraw_company_package (OCP-599).
 * Rendered on its own page (/company-packages) and on the account detail page
 * (with `customerId`, where a new offer is made for that account).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { fmtDate, fmtDateTime } from '../lib/format-time';

export type CompanyPackageStatus = 'offered' | 'accepted' | 'withdrawn' | 'expired';

export interface CompanyPackageRow {
  id: string;
  customer_id: string;
  customer_email: string | null;
  company_name: string | null;
  product_id: string;
  product_display_name: string | null;
  billing_period: string | null;
  seat_count: number;
  price_per_seat_cents: number;
  total_per_period_cents: number;
  price_input: 'per_seat' | 'total';
  term_months: number;
  includes_onboarding: boolean;
  includes_training: boolean;
  customer_note: string | null;
  status: CompanyPackageStatus;
  offer_expires_at: string;
  source: 'mcp' | 'admin-ui';
  created_by: string;
  reason: string;
  accepted_order_id: string | null;
  accepted_at: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  withdraw_reason: string | null;
  created_at: string;
}

interface ListResponse {
  items: CompanyPackageRow[];
  total: number;
}

interface CatalogProduct {
  id: string;
  display_name: string;
  product_family: string;
  price_eur_cents: number;
  billing_period: string;
  features: Record<string, unknown>;
  active: boolean;
}

const STATUSES: CompanyPackageStatus[] = ['offered', 'accepted', 'withdrawn', 'expired'];
const MIN_REASON = 10;

const STATUS_CLASSES: Record<CompanyPackageStatus, string> = {
  offered: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  accepted: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  withdrawn: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  expired: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

export function eur(cents: number): string {
  return `EUR ${(cents / 100).toFixed(2)}`;
}

function periodWord(period: string | null): string {
  return period === 'yearly' ? 'year' : 'month';
}

function includesLabel(row: Pick<CompanyPackageRow, 'includes_onboarding' | 'includes_training'>): string {
  const parts = [row.includes_onboarding ? 'onboarding' : null, row.includes_training ? 'training' : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' + ') : 'nothing extra';
}

function errorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const body = err.body as { error?: string; detail?: Record<string, unknown> } | null;
  const code = body?.error;
  if (code === 'offer_exists') return 'This account already has a live offer. Withdraw it first, then offer the new package.';
  if (code === 'total_not_divisible') {
    const d = body?.detail ?? {};
    const lower = typeof d.nearest_lower_total_cents === 'number' ? eur(d.nearest_lower_total_cents) : null;
    const higher = typeof d.nearest_higher_total_cents === 'number' ? eur(d.nearest_higher_total_cents) : null;
    return `The total does not split into whole cents per seat. Nearest totals that do: ${[lower, higher].filter(Boolean).join(' or ')}.`;
  }
  if (code === 'term_not_whole_periods') return 'The term must be a whole number of billing periods (12, 24, ... months for a yearly plan).';
  if (code === 'product_not_seat_based') return 'A package needs a plan billed per seat (Team or Business).';
  if (code === 'price_required') return 'Give the price either per seat or as the total, not both.';
  if (code === 'customer_not_found') return 'No account with this id.';
  if (code === 'company_package_accepted') return 'An order already paid this package; a concluded contract is not withdrawn.';
  return `${fallback} (HTTP ${err.status}${code ? `, ${code}` : ''}).`;
}

export function CompanyPackagesPanel({ customerId, compact = false }: { customerId?: string; compact?: boolean }) {
  const [status, setStatus] = useState<CompanyPackageStatus | 'all'>('all');
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offering, setOffering] = useState(false);
  const [withdrawing, setWithdrawing] = useState<CompanyPackageRow | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (customerId) params.set('customer_id', customerId);
      if (status !== 'all') params.set('status', status);
      const q = params.toString();
      setData(await api<ListResponse>(`/v1/admin/company-packages${q ? `?${q}` : ''}`));
    } catch (err) {
      setError(errorMessage(err, 'Could not load the company packages'));
    }
  }, [customerId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div data-testid="company-packages-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!compact && (
          <div className="flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Status">
            {(['all', ...STATUSES] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                aria-pressed={status === s}
                className={`rounded px-3 py-1 ring-1 ring-inset ${status === s ? 'bg-brand-500 text-white ring-brand-500' : 'text-gray-700 ring-gray-300 dark:text-gray-300 dark:ring-gray-700'}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setOffering(true)}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Offer a package
        </button>
      </div>

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && data.items.length === 0 && (
        <p className="mt-3 text-sm text-gray-500" data-testid="company-packages-empty">
          No company package{customerId ? ' for this account' : ' in this view'}. Every seat count is charged the list price.
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                {!customerId && <th className="px-3 py-2">Account</th>}
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2">Seats</th>
                <th className="px-3 py-2">Price</th>
                <th className="px-3 py-2">Term</th>
                <th className="px-3 py-2">Includes</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Offer until</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.items.map((p) => (
                <tr key={p.id} data-testid="company-package-row">
                  {!customerId && (
                    <td className="px-3 py-2">
                      <Link to={`/accounts/${p.customer_id}`} className="underline">
                        {p.company_name ?? p.customer_email ?? p.customer_id}
                      </Link>
                    </td>
                  )}
                  <td className="px-3 py-2">{p.product_display_name ?? p.product_id}</td>
                  <td className="px-3 py-2">{p.seat_count}</td>
                  <td className="px-3 py-2">
                    {eur(p.price_per_seat_cents)} per seat per {periodWord(p.billing_period)}
                    <span className="ml-1 text-xs text-gray-500">({eur(p.total_per_period_cents)} total)</span>
                  </td>
                  <td className="px-3 py-2">{p.term_months} months</td>
                  <td className="px-3 py-2">{includesLabel(p)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[p.status]}`}>{p.status}</span>
                    {p.withdraw_reason && <span className="ml-1 text-xs text-gray-500">{p.withdraw_reason}</span>}
                    {p.accepted_at && <span className="ml-1 text-xs text-gray-500">{fmtDateTime(p.accepted_at)}</span>}
                  </td>
                  <td className="px-3 py-2">{fmtDate(p.offer_expires_at)}</td>
                  <td className="px-3 py-2 text-right">
                    {(p.status === 'offered' || p.status === 'expired') && (
                      <button
                        type="button"
                        onClick={() => setWithdrawing(p)}
                        className="text-xs text-red-700 hover:underline dark:text-red-300"
                      >
                        Withdraw
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {offering && (
        <OfferDialog
          customerId={customerId}
          onClose={() => setOffering(false)}
          onDone={() => {
            setOffering(false);
            void load();
          }}
        />
      )}
      {withdrawing && (
        <WithdrawDialog
          row={withdrawing}
          onClose={() => setWithdrawing(null)}
          onDone={() => {
            setWithdrawing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

const INPUT = 'mt-1 block w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';

/** Parse "EUR 12.34" style input into whole cents; null = not a positive amount. */
function parseCents(raw: string): number | null {
  const n = Number(raw.replace(',', '.').trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function OfferDialog({ customerId, onClose, onDone }: { customerId?: string; onClose: () => void; onDone: () => void }) {
  const [products, setProducts] = useState<CatalogProduct[] | null>(null);
  const [account, setAccount] = useState(customerId ?? '');
  const [productId, setProductId] = useState('');
  const [seats, setSeats] = useState('');
  const [priceMode, setPriceMode] = useState<'per_seat' | 'total'>('per_seat');
  const [price, setPrice] = useState('');
  const [term, setTerm] = useState('12');
  const [onboarding, setOnboarding] = useState(false);
  const [training, setTraining] = useState(false);
  const [note, setNote] = useState('');
  const [validDays, setValidDays] = useState('30');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<{ items: CatalogProduct[] } | CatalogProduct[]>('/v1/admin/products');
        const items = Array.isArray(res) ? res : res.items;
        const seatPlans = items.filter((p) => p.active && (p.features as { per_seat?: unknown } | null)?.per_seat === true);
        setProducts(seatPlans);
        if (seatPlans[0]) setProductId(seatPlans[0].id);
      } catch (err) {
        setError(errorMessage(err, 'Could not load the plans'));
      }
    })();
  }, []);

  const product = products?.find((p) => p.id === productId) ?? null;
  const seatCount = Number(seats);
  const cents = parseCents(price);
  const valid =
    account.trim().length > 0 &&
    product !== null &&
    Number.isInteger(seatCount) &&
    seatCount >= 1 &&
    cents !== null &&
    Number.isInteger(Number(term)) &&
    Number(term) >= 1 &&
    reason.trim().length >= MIN_REASON;
  const perSeatPreview = cents !== null && seatCount >= 1 ? (priceMode === 'per_seat' ? cents : cents / seatCount) : null;

  async function submit() {
    if (!valid || cents === null) return;
    setBusy(true);
    setError(null);
    try {
      await api('/v1/admin/company-packages', {
        method: 'POST',
        body: {
          customer_id: account.trim(),
          product_id: productId,
          seat_count: seatCount,
          ...(priceMode === 'per_seat' ? { price_per_seat_cents: cents } : { total_per_period_cents: cents }),
          term_months: Number(term),
          includes_onboarding: onboarding,
          includes_training: training,
          customer_note: note.trim() ? note.trim() : null,
          valid_days: Number(validDays) || 30,
          reason: reason.trim(),
        },
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not offer the package'));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Offer a company package" onClose={onClose}>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        The account&apos;s checkout for this plan will charge the package: the seat count is fixed and no discount code applies on top.
        Running subscriptions keep their price.
      </p>
      {!customerId && (
        <label className="mt-3 block text-sm">
          Account id
          <input value={account} onChange={(e) => setAccount(e.target.value)} placeholder="Portal account id (from Accounts)" className={INPUT} />
        </label>
      )}
      <label className="mt-3 block text-sm">
        Plan
        <select value={productId} onChange={(e) => setProductId(e.target.value)} className={INPUT} aria-label="Plan">
          {(products ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name} ({p.billing_period}, list {eur(p.price_eur_cents)} per seat)
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block text-sm">
        Seats
        <input type="number" min={1} max={10000} value={seats} onChange={(e) => setSeats(e.target.value)} className={INPUT} aria-label="Seats" />
      </label>
      <fieldset className="mt-3 text-sm">
        <legend>Price per billing period</legend>
        <div className="mt-1 flex gap-4">
          <label className="flex items-center gap-1">
            <input type="radio" checked={priceMode === 'per_seat'} onChange={() => setPriceMode('per_seat')} /> per seat
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={priceMode === 'total'} onChange={() => setPriceMode('total')} /> total
          </label>
        </div>
        <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="EUR, e.g. 15.00" className={INPUT} aria-label="Price in EUR" />
        {perSeatPreview !== null && product && (
          <p className="mt-1 text-xs text-gray-500" data-testid="company-package-preview">
            {seatCount} seats at {eur(Math.round(perSeatPreview))} per seat per {periodWord(product.billing_period)}
            {!Number.isInteger(perSeatPreview) ? ' (the total must split into whole cents per seat)' : ''}; list price {eur(product.price_eur_cents)}.
          </p>
        )}
      </fieldset>
      <label className="mt-3 block text-sm">
        Term (months)
        <input type="number" min={1} max={120} value={term} onChange={(e) => setTerm(e.target.value)} className={INPUT} aria-label="Term in months" />
      </label>
      <div className="mt-3 flex gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={onboarding} onChange={(e) => setOnboarding(e.target.checked)} /> Onboarding included
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={training} onChange={(e) => setTraining(e.target.checked)} /> Training included
        </label>
      </div>
      <label className="mt-3 block text-sm">
        Note to the customer (on the order summary and the confirmation)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} className={INPUT} />
      </label>
      <label className="mt-3 block text-sm">
        Offer open for (days)
        <input type="number" min={1} max={365} value={validDays} onChange={(e) => setValidDays(e.target.value)} className={INPUT} aria-label="Offer open for days" />
      </label>
      <label className="mt-3 block text-sm">
        Reason (recorded on the audit row)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={INPUT} aria-label="Reason" />
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
        <button type="button" disabled={busy || !valid} onClick={() => void submit()} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
          Offer
        </button>
      </div>
    </Dialog>
  );
}

function WithdrawDialog({ row, onClose, onDone }: { row: CompanyPackageRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/company-packages/${row.id}/withdraw`, { method: 'POST', body: { reason: reason.trim() } });
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not withdraw the package'));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Withdraw the company package" onClose={onClose}>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        The account&apos;s checkout is the list price again. A checkout session already open can still be paid; its payment accepts the package.
      </p>
      <label className="mt-3 block text-sm">
        Reason (recorded on the audit row)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={INPUT} aria-label="Reason" />
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm">Cancel</button>
        <button type="button" disabled={busy || reason.trim().length < MIN_REASON} onClick={() => void submit()} className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
          Withdraw
        </button>
      </div>
    </Dialog>
  );
}
